import React, { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { cn } from "../lib/utils";
import { Plus, Pencil, Trash2, Undo2, Loader2, Download } from "lucide-react";

const ITEM_TYPES = [
  { value: "keyboard", label: "Keyboard" },
  { value: "mouse", label: "Mouse" },
  { value: "webcam", label: "Webcam" },
  { value: "headset", label: "Headset" },
  { value: "dock", label: "Dock" },
  { value: "monitor", label: "Monitor" },
  { value: "other", label: "Other" },
];

const DEFAULT_FORM = {
  employee_name: "",
  item_type: "keyboard",
  description: "",
  device_id: "",
  date_issued: new Date().toISOString().slice(0, 10),
  status: "assigned",
  notes: "",
};

function PeripheralFormModal({ open, onClose, onSaved, record, devices, lockedDeviceId }) {
  const { toast } = useToast();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (record) {
      setForm({
        employee_name: record.employee_name || "",
        item_type: record.item_type || "keyboard",
        description: record.description || "",
        device_id: record.device_id || "",
        date_issued: record.date_issued || new Date().toISOString().slice(0, 10),
        status: record.status || "assigned",
        notes: record.notes || "",
      });
    } else {
      setForm({ ...DEFAULT_FORM, device_id: lockedDeviceId || "" });
    }
  }, [open, record, lockedDeviceId]);

  const patch = (p) => setForm((f) => ({ ...f, ...p }));

  const save = async () => {
    if (!form.employee_name.trim()) {
      toast({ title: "Employee name is required", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form, device_id: form.device_id || null };
      if (record?.id) {
        await api.put(`/vulnerability/peripherals/${record.id}`, payload);
        toast({ title: "Peripheral updated" });
      } else {
        await api.post("/vulnerability/peripherals", payload);
        toast({ title: "Peripheral logged" });
      }
      onSaved?.();
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg" data-testid="peripheral-form-modal">
        <DialogHeader>
          <DialogTitle>{record?.id ? "Edit peripheral" : "Log a peripheral"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div>
            <Label>Employee</Label>
            <Input
              value={form.employee_name}
              onChange={(e) => patch({ employee_name: e.target.value })}
              placeholder="e.g. Jane Doe"
              className="mt-1"
              autoFocus
              data-testid="peripheral-form-employee-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Item type</Label>
              <Select value={form.item_type} onValueChange={(v) => patch({ item_type: v })}>
                <SelectTrigger className="mt-1" data-testid="peripheral-form-item-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ITEM_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date issued</Label>
              <Input type="date" value={form.date_issued} onChange={(e) => patch({ date_issued: e.target.value })} className="mt-1" data-testid="peripheral-form-date-input" />
            </div>
          </div>
          <div>
            <Label>Description / serial</Label>
            <Input
              value={form.description}
              onChange={(e) => patch({ description: e.target.value })}
              placeholder="e.g. Apple Magic Keyboard, S/N ABC123"
              className="mt-1"
              data-testid="peripheral-form-description-input"
            />
          </div>
          {!lockedDeviceId && (
            <div>
              <Label>Linked workstation (optional)</Label>
              <Select value={form.device_id || "__none__"} onValueChange={(v) => patch({ device_id: v === "__none__" ? "" : v })}>
                <SelectTrigger className="mt-1" data-testid="peripheral-form-device-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {devices.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => patch({ status: v })}>
              <SelectTrigger className="mt-1" data-testid="peripheral-form-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="returned">Returned</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={(e) => patch({ notes: e.target.value })} rows={2} className="mt-1" data-testid="peripheral-form-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="peripheral-form-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={busy} className="gap-1.5" data-testid="peripheral-form-save-btn">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Reusable peripherals log. Pass `deviceId` to scope it to one workstation
 * (used inside the device detail modal); omit it for the org-wide master list.
 */
export default function PeripheralsPanel({ deviceId, isAdmin, embedded = false }) {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = deviceId ? { device_id: deviceId } : {};
      const [periphRes, devicesRes] = await Promise.all([
        api.get("/vulnerability/peripherals", { params }),
        deviceId ? Promise.resolve(null) : api.get("/vulnerability/workstations/summary"),
      ]);
      setItems(periphRes.data?.peripherals || []);
      if (devicesRes) setDevices(devicesRes.data?.devices || []);
    } catch (e) {
      toast({ title: "Couldn't load peripherals", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setLoading(false); }
  }, [deviceId, toast]);

  useEffect(() => { load(); }, [load]);

  const markReturned = async (item) => {
    try {
      await api.put(`/vulnerability/peripherals/${item.id}`, { status: "returned" });
      toast({ title: "Marked returned" });
      load();
    } catch (e) {
      toast({ title: "Couldn't update", description: e?.response?.data?.detail || "", variant: "destructive" });
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/vulnerability/peripherals/${id}`);
      toast({ title: "Peripheral deleted" });
      setConfirmDeleteId(null);
      load();
    } catch (e) {
      toast({ title: "Couldn't delete", description: e?.response?.data?.detail || "", variant: "destructive" });
    }
  };

  const exportCsv = async () => {
    try {
      const params = deviceId ? { device_id: deviceId } : {};
      const { data } = await api.get("/vulnerability/peripherals/export.csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `peripherals-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: "Export failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    }
  };

  return (
    <div className={cn(!embedded && "rounded-xl border border-border bg-card")} data-testid={deviceId ? "device-peripherals-panel" : "peripherals-master-panel"}>
      <div className={cn("flex items-center justify-between gap-2", embedded ? "mb-2" : "px-4 py-3 border-b border-border")}>
        <div className="text-[13px] font-semibold">{embedded ? "Peripherals" : `Peripherals (${items.length})`}</div>
        <div className="flex items-center gap-1.5">
          <Button
            size="sm"
            variant="outline"
            onClick={exportCsv}
            disabled={items.length === 0}
            className="h-7 gap-1.5"
            data-testid={deviceId ? "device-peripheral-export-csv-btn" : "peripherals-export-csv-btn"}
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </Button>
          {isAdmin && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setEditing(null); setFormOpen(true); }}
              className="h-7 gap-1.5"
              data-testid={deviceId ? "device-peripheral-add-btn" : "peripherals-add-btn"}
            >
              <Plus className="w-3.5 h-3.5" /> Log peripheral
            </Button>
          )}
        </div>
      </div>

      <div className={embedded ? "" : "p-4"}>
        {loading ? (
          <div className="space-y-2">{[0, 1].map((i) => <div key={i} className="h-12 rounded-md bg-muted/40 animate-pulse" />)}</div>
        ) : items.length === 0 ? (
          <div className="py-6 text-center text-[12.5px] text-muted-foreground">No peripherals logged yet.</div>
        ) : (
          <ul className="space-y-1.5" data-testid="peripherals-table">
            {items.map((p) => (
              <li key={p.id} className="rounded-md border border-border bg-background/60 px-3 py-2.5" data-testid={`peripherals-row-${p.id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[13px] font-medium">{p.employee_name}</span>
                      <span className="text-[10px] rounded border border-border bg-muted/50 px-1.5 py-0.5 font-mono uppercase">{p.item_type}</span>
                      <span className={cn(
                        "text-[10px] rounded-full border px-1.5 py-0.5 font-semibold uppercase",
                        p.status === "returned" ? "border-border text-muted-foreground" : "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                      )}>
                        {p.status}
                      </span>
                    </div>
                    <div className="text-[11.5px] text-muted-foreground mt-0.5 truncate">
                      {[p.description, !deviceId && p.device_name, `issued ${p.date_issued}`, p.status === "returned" && p.date_returned && `returned ${p.date_returned}`].filter(Boolean).join(" · ")}
                    </div>
                    {p.notes && <div className="text-[11.5px] text-muted-foreground mt-0.5 italic">{p.notes}</div>}
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-1 shrink-0">
                      {p.status === "assigned" && (
                        <button
                          type="button"
                          onClick={() => markReturned(p)}
                          title="Mark returned"
                          data-testid={`peripheral-mark-returned-btn-${p.id}`}
                          className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground"
                        >
                          <Undo2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => { setEditing(p); setFormOpen(true); }}
                        title="Edit"
                        data-testid={`peripheral-edit-btn-${p.id}`}
                        className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      {confirmDeleteId === p.id ? (
                        <button
                          type="button"
                          onClick={() => remove(p.id)}
                          data-testid={`peripheral-delete-confirm-btn-${p.id}`}
                          className="text-[11px] font-semibold text-destructive px-1.5"
                        >
                          Confirm?
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteId(p.id)}
                          title="Delete"
                          data-testid={`peripheral-delete-btn-${p.id}`}
                          className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <PeripheralFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={() => { setFormOpen(false); load(); }}
        record={editing}
        devices={devices}
        lockedDeviceId={deviceId}
      />
    </div>
  );
}
